import type { Perm } from "../access/api";
import { MapMode, mapMode, mapOpens } from "../map/mapAccess";
import { ReportsMode, reportsInMenu } from "../reports/reportsAccess";
import { DashboardMode, dashboardMode } from "../dashboard/dashboardMode";

export type NavKey =
  | "dashboard" | "contacts" | "map" | "routes" | "reports" | "team" | "billing" | "export" | "volunteering" | "tutorials"
  | "adminDashboard" | "workspaces" | "users" | "packages" | "uploads" | "pending" | "centres" | "connections" | "suspect" | "geocoding"
  | "donations" | "people" | "downloads" | "activity" | "problems" | "billingSettings" | "accessConsole" | "messages" | "preview" | "audience" | "routeFeatures";
/** The routes list. */
export const ROUTES_PATH = "/saved-routes";

/** `label`: the words to show when they differ from the key's own ("My routes" for a person who follows routes but does not plan them). */
export type NavItem = { key: NavKey; to?: string; action?: "export"; label?: "myRoutes" | "home" };
export type NavContext = {
  has(p: string | string[]): boolean;
  canAccessAny(p: string, s: "routes" | "households"): boolean;
  showsVolunteering: boolean;
  workspaceRole: string | null;
  isVolunteerOnly: boolean;
  isSuperAdmin: boolean;
  /**
   * K1.1: the staff areas switched on for this staff person (accounts, people, billing, data_loading, tools, centres).
   * null/absent = a full Super Admin: everything, and the Activity page.
   */
  staffAccess?: string[] | null;
  /** Staff pages that exist only in the new layer are listed once their address is served (not a link to the old admin start page). Absent = all. */
  livePaths?: ReadonlySet<string>;
  locked: boolean;
  contactCount: number;
  /**
   * The access model's answer (5A): present once `me/access` has loaded, absent while loading or when it failed
   * (then today's rule below applies, so the menu never flashes). Items follow permissions, never role names.
   */
  can?(p: Perm): boolean;
  /** False for a personal account: no team, so no "My team" (5A). */
  teamAllowed?: boolean;
  /** The access answer's "has at least one route to show" (A3). Absent = unknown = shown. */
  hasRoutes?: boolean;
  /** Reaches every contact and route. Today's /map only opens for people who plan with full reach (pre-flight I8); the new Map opens for every planner. */
  reachAll?: boolean;
  /** Which Map the switchboard serves; defaults to its line (tests pass it). */
  mapMode?: MapMode;
  /** Which Reports page the switchboard serves; defaults to its line (tests pass it). */
  reportsMode?: ReportsMode;
  /** Which Dashboard the switchboard serves; defaults to its line (tests pass it). */
  dashboardMode?: DashboardMode;
};

const DASHBOARD_ROLES = ["admin", "anash", "super_admin"];

/** Order and rules approved in the frame review (1.1–1.12, 1.10, 3.4). */
export function buildMainNav(c: NavContext): NavItem[] {
  if (c.locked) {
    const out: NavItem[] = [];
    if (c.has("read_billing")) out.push({ key: "billing", to: "/billing" });
    // Walkthrough m13: with the access answer, only people holding the Download switch (a Volunteer on a paused account does not).
    if (c.contactCount > 0 && (!c.can || c.can("contacts.export"))) out.push({ key: "export", action: "export" });
    return out;
  }
  if (c.isVolunteerOnly) return c.has("manage_volunteering") ? [{ key: "volunteering", to: "/volunteering" }] : [];
  const out: NavItem[] = [];
  const can = c.can;
  if (can) {
    // Menu by permission (5A F3). Reports keeps today's right too: a member sees it only where the old page lets them in.
    // Dashboard follows "can approve" until stage 6 defines the member home; staff keep it from any account.
    // Once the new Home is served (decision S1.2): one item "Home" for everyone who gets past the volunteer-only gate above; the page fits the person.
    if ((c.dashboardMode ?? dashboardMode()) === "next") out.push({ key: "dashboard", to: "/dashboard", label: "home" });
    else if (c.isSuperAdmin || can("contacts.approve")) out.push({ key: "dashboard", to: "/dashboard" });
    if (can("contacts.read")) out.push({ key: "contacts", to: "/households" });
    if (mapOpens(can("routes.plan"), c.reachAll !== false, c.mapMode ?? mapMode())) out.push({ key: "map", to: "/map" });
    // Owner note A3: with Plan routes it is "Routes". Without it, "My routes" (the ones given to the person), and only when there are some.
    // Contacts-only switches (no Visits, Change routes or Plan routes) have no routes at all: no item, whatever was given.
    if (can("routes.read") && (can("routes.follow") || can("routes.change") || can("routes.plan"))) {
      if (can("routes.plan")) out.push({ key: "routes", to: ROUTES_PATH });
      else if (c.hasRoutes !== false) out.push({ key: "routes", to: ROUTES_PATH, label: "myRoutes" });
    }
    // Today's page: See visit history and today's own right. The new page: See visit history ("Reports") or Write reports ("My reports"), nothing else.
    if (reportsInMenu((p) => can(p), c.has("read_duchot"), c.reportsMode)) out.push({ key: "reports", to: "/duchot" });
    if (can("team.see") && c.teamAllowed !== false) out.push({ key: "team", to: "/my-team" });
    if (can("billing.view")) out.push({ key: "billing", to: "/billing" });
    if (c.has("manage_volunteering") && c.showsVolunteering) out.push({ key: "volunteering", to: "/volunteering" });
    return out;
  }
  // Same rule as the /dashboard guard (RoleRoute): a super admin gets in from any workspace.
  if (c.isSuperAdmin || (c.workspaceRole && DASHBOARD_ROLES.includes(c.workspaceRole))) out.push({ key: "dashboard", to: "/dashboard" });
  if (c.canAccessAny("read_households", "households")) out.push({ key: "contacts", to: "/households" });
  if (c.has("read_map")) out.push({ key: "map", to: "/map" });
  if (c.canAccessAny("read_routes", "routes")) out.push({ key: "routes", to: ROUTES_PATH });
  if (c.has("read_duchot")) out.push({ key: "reports", to: "/duchot" });
  if (c.has("read_my_team")) out.push({ key: "team", to: "/my-team" });
  if (c.has("read_billing")) out.push({ key: "billing", to: "/billing" });
  if (c.has("manage_volunteering") && c.showsVolunteering) out.push({ key: "volunteering", to: "/volunteering" });
  return out;
}

/** Tutorials moved from the menu into Help (layout F); the old rule is unchanged: not while locked, not for volunteers-only, needs contact access. */
export function showsTutorials(c: NavContext): boolean {
  return !c.locked && !c.isVolunteerOnly && c.has("read_households");
}

export function buildAdminNav(c: NavContext): NavItem[] {
  if (!c.isSuperAdmin) return [];
  // [key, path, today's permission, staff area ("full" = a full Super Admin only), new-layer page?]
  const rows: [NavKey, string, string, string, boolean][] = [
    ["adminDashboard", "/admin/dashboard", "read_admin_workspaces", "accounts", false],
    ["workspaces", "/admin/workspaces", "read_admin_workspaces", "accounts", false],
    ["users", "/admin/users", "read_admin_users", "people", false],
    ["people", "/admin/people", "read_admin_users", "people", true],
    ["packages", "/admin/packages", "read_admin_packages", "billing", false],
    ["donations", "/admin/donations", "read_admin_packages", "billing", true],
    ["uploads", "/admin/managed-uploads", "read_admin_packages", "data_loading", false],
    ["pending", "/admin/pending-users", "read_admin_users", "people", false],
    ["centres", "/admin/centers", "read_admin_users", "centres", false],
    ["connections", "/admin/center-connections", "read_admin_users", "centres", false],
    ["suspect", "/admin/routing/suspect-addresses", "read_admin_workspaces", "tools", false],
    ["geocoding", "/admin/geocoding-shadow", "read_admin_workspaces", "tools", false],
    ["downloads", "/admin/downloads", "read_admin_workspaces", "tools", true],
    ["activity", "/admin/activity", "read_admin_workspaces", "full", true],
    // The Problems log (owner 2026-11-02, P1): what failed in any account. Full Super Admin only; after Activity.
    ["problems", "/admin/problems", "read_admin_workspaces", "full", true],
    // Walkthrough public #8: the staff pages that exist only in the new layer get a menu entry once their line is live.
    ["billingSettings", "/admin/billing/setup-fee", "read_admin_packages", "billing", true],
    ["accessConsole", "/admin/access", "read_admin_workspaces", "full", true],
    // The staff inbox (Contact us messages); its server routes are in the "accounts" staff area. Walkthrough 2026-10-29, issue 13.
    ["messages", "/admin/messages", "read_admin_workspaces", "accounts", true],
    // Preview mode (soft launch): who sees the new design before the release. Full Super Admin only.
    ["preview", "/admin/preview", "read_admin_workspaces", "full", true],
    // Everyone with a login: filters, export (CSV / Excel) and e-mailing them. Full Super Admin only (owner 2026-11-02).
    ["audience", "/admin/audience", "read_admin_workspaces", "full", true],
    // Car / On foot switches (owner 2026-11-02): each piece off for everyone without a code change. Full Super Admin only.
    ["routeFeatures", "/admin/route-features", "read_admin_workspaces", "full", true],
  ];
  const full = c.staffAccess == null;
  return rows
    .filter(([, to, p, area, isNew]) => c.has(p) && (full || (area !== "full" && c.staffAccess!.includes(area))) && (!isNew || !c.livePaths || c.livePaths.has(to)))
    .map(([key, to]) => ({ key, to }));
}

/** /next/<path> and /legacy/<path> (the switchboard previews) belong to the same menu item as /<path>. */
const PREVIEW_PREFIX = /^\/(next|legacy)(?=\/)/;

export function matchNav(items: NavItem[], rawPathname: string): NavItem | undefined {
  const pathname = rawPathname.replace(PREVIEW_PREFIX, "");
  return items
    .filter((i) => i.to && (pathname === i.to || pathname.startsWith(i.to + "/")))
    .sort((a, b) => (b.to!.length - a.to!.length))[0];
}
